/**
 * First URL path segments that are global dashboard routes, not `/:orgSlug/...`
 * workspace org slugs. Keep in sync with single-spa `activeWhen` for root-level
 * apps (e.g. setup on `/onboarding/*`).
 */
const RESERVED_NON_ORG_FIRST_SEGMENTS = new Set([
  'onboarding',
  'setup',
  'magic',
]);

export function isReservedNonOrgPathSegment(segment: string): boolean {
  return RESERVED_NON_ORG_FIRST_SEGMENTS.has(segment);
}

/**
 * Returns the workspace org slug from a dashboard pathname, or `undefined` when
 * the first segment is a reserved global route (e.g. `/onboarding/create-organization`).
 */
export function getWorkspaceOrgSlugFromPathname(
  pathname: string
): string | undefined {
  const first = pathname.split('/').filter(Boolean)[0];
  if (!first || isReservedNonOrgPathSegment(first)) {
    return undefined;
  }
  return first;
}

/**
 * Workspace org + tenant auth is not used for these shell paths (setup/onboarding,
 * root setup, magic links). The shell should not block the MFE on `authResolved`.
 */
export function isShellPathWithoutWorkspaceOrg(pathname: string): boolean {
  const first = pathname.split('/').filter(Boolean)[0];
  return !!first && isReservedNonOrgPathSegment(first);
}

/**
 * Workspace routes like `/:orgSlug/setup/create-building` (first segment is a
 * real org slug, second is `setup`). The setup MFE resolves org/auth locally;
 * the shell must not treat a transient null `organizationBySlug` as fatal or
 * block the UI on `authResolved` (e.g. right after org creation from onboarding).
 */
export function isWorkspaceOrgSetupPath(pathname: string): boolean {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length < 2 || parts[1] !== 'setup') {
    return false;
  }
  return !isReservedNonOrgPathSegment(parts[0]);
}
